import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api-client';
import { formatDiscussionTime } from '../../utils/timeFormat';
import { useQuestionDiscussion } from '../../hooks/useQuestionDiscussion';
import toast from 'react-hot-toast';
import SendButton from '../shared/SendButton';
import MessageContextMenu from './MessageContextMenu';
import ImageModal from '../shared/ImageModal';
import Badge from '../shared/Badge';

export default function DiscussionView({ question, onQuestionUpdated }) {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [contextMenu, setContextMenu] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [modalImageUrl, setModalImageUrl] = useState(null);

  // Handle incoming messages from WebSocket
  const handleMessageAdded = useCallback((newMessage) => {
    if (!question) return;

    // Update the question with the new message
    const updatedQuestion = {
      ...question,
      discussion_messages: [...(question.discussion_messages || []), newMessage]
    };

    if (onQuestionUpdated) {
      onQuestionUpdated(updatedQuestion);
    }
  }, [question, onQuestionUpdated]);

  // Setup WebSocket connection for real-time updates
  const { sendTyping, on } = useQuestionDiscussion(
    question?.id,
    handleMessageAdded
  );

  // Handle typing indicators
  useEffect(() => {
    if (!question?.id) return;

    const handleTyping = (data) => {
      if (data.question_id !== question.id) return;
      if (data.user === user?.username) return; // Ignore own typing events

      setTypingUsers(prev => {
        const newSet = new Set(prev);
        if (data.is_typing) {
          newSet.add(data.user);
        } else {
          newSet.delete(data.user);
        }
        return newSet;
      });

      // Auto-remove typing indicator after 4 seconds
      if (data.is_typing) {
        setTimeout(() => {
          setTypingUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(data.user);
            return newSet;
          });
        }, 4000);
      }
    };

    const cleanup = on('question_typing', handleTyping);

    return () => {
      cleanup();
    };
  }, [question?.id, user?.username, on]);

  // Handle input change and send typing indicator
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setMessage(newValue);

    // Send typing indicator
    if (newValue.trim() && !isSending) {
      sendTyping(true);
    } else if (!newValue.trim()) {
      sendTyping(false);
    }
  };

  // Handle right-click on message
  const handleMessageContextMenu = (e, msg) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      message: msg
    });
  };

  // Edit message
  const handleEditMessage = (msg) => {
    setEditingMessage(msg.id);
    setEditContent(msg.content);
  };

  const handleSaveEdit = async (messageId) => {
    if (!editContent.trim()) return;

    try {
      const updatedQuestion = await api.put(
        `/api/v1/questions/${question.id}/messages/${messageId}`,
        { content: editContent.trim() }
      );

      if (onQuestionUpdated) {
        onQuestionUpdated(updatedQuestion);
      }

      setEditingMessage(null);
      setEditContent('');
      toast.success('Message updated');
    } catch (error) {
      console.error('Failed to edit message:', error);
      toast.error('Failed to edit message');
    }
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setEditContent('');
  };

  // Delete message
  const handleDeleteMessage = async (messageId) => {
    try {
      const updatedQuestion = await api.delete(
        `/api/v1/questions/${question.id}/messages/${messageId}`
      );

      if (onQuestionUpdated) {
        onQuestionUpdated(updatedQuestion);
      }

      toast.success('Message deleted');
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error('Failed to delete message');
    }
  };

  // Use as answer
  const handleUseAsAnswer = async (messageId) => {
    try {
      const updatedQuestion = await api.post(
        `/api/v1/questions/${question.id}/answer`,
        { message_id: messageId }
      );

      if (onQuestionUpdated) {
        onQuestionUpdated(updatedQuestion);
      }

      toast.success('Message set as answer');
    } catch (error) {
      console.error('Failed to set answer:', error);
      toast.error('Failed to set answer');
    }
  };

  // Helper function to generate default avatar
  const getDefaultAvatar = (username) => {
    const initial = username?.charAt(0).toUpperCase() || 'U';
    return (
      <div
        className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center font-semibold text-base flex-shrink-0"
        style={{ color: 'var(--color-text-on-accent)' }}
      >
        {initial}
      </div>
    );
  };

  // Helper function to get avatar for a message author
  const getAuthorAvatar = (authorName) => {
    // If the author is the current user and they have an avatar URL, use it
    if (authorName === user?.username && user?.avatar_url) {
      return (
        <img
          src={user.avatar_url}
          alt={authorName}
          className="w-10 h-10 rounded-full object-cover border-2 border-gray-700 flex-shrink-0"
        />
      );
    }
    // Otherwise, show default avatar with author's initial
    return getDefaultAvatar(authorName);
  };

  if (!question) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <svg
            className="w-24 h-24 mx-auto mb-4"
            style={{ color: 'var(--color-text-disabled)' }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-muted)' }}>No Question Selected</h3>
          <p style={{ color: 'var(--color-text-muted)' }}>Select a question from the sidebar to view the discussion</p>
        </div>
      </div>
    );
  }

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setSelectedImage(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Remove selected image
  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!message.trim() && !selectedImage) || isSending) return;

    setIsSending(true);
    // Stop typing indicator
    sendTyping(false);

    try {
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('content', message.trim());
      if (selectedImage) {
        formData.append('image', selectedImage);
      }

      // Call API to add message
      await api.post(
        `/api/v1/questions/${question.id}/messages`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Don't update state here - let WebSocket handle it to avoid duplicates
      // The backend will broadcast the message, and handleMessageAdded will update the UI

      // Clear input and image
      setMessage('');
      setSelectedImage(null);
      setImagePreview(null);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="w-full h-full max-w-5xl bg-gray-800/30 border border-gray-700 rounded-2xl flex flex-col overflow-hidden relative">
      {/* Question Header - Fixed at top */}
      <div className="px-6 py-5 border-b border-gray-700 flex-shrink-0">
        <div className="flex items-start justify-between gap-4 mb-3">
          <h2 className="text-2xl font-bold text-white flex-1">{question.title}</h2>
          {question.is_answered && (
            <span className="flex-shrink-0 px-3 py-1 text-sm font-semibold rounded-full bg-green-500/20 text-green-400 border border-green-500/30">
              Answered
            </span>
          )}
        </div>

        {/* Pills for Product and Category */}
        <div className="flex flex-wrap gap-2">
          {question.product && (
            <Badge variant="blue">
              <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
              </svg>
              {question.product}
            </Badge>
          )}

          {question.category && (
            <Badge variant="purple">
              <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M7 3a1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
              </svg>
              {question.category}
            </Badge>
          )}
        </div>
      </div>

      {/* Discussion Messages - Scrollable middle section with padding for input */}
      <div className="flex-1 overflow-y-auto px-6 py-6 pb-32">
        <div className="space-y-4">
          {question.discussion_messages && question.discussion_messages.length > 0 ? (
            question.discussion_messages.map((msg) => (
              <div
                key={msg.id}
                className="flex gap-3"
              >
                {/* Avatar */}
                {getAuthorAvatar(msg.author)}

                {/* Message Content */}
                <div
                  className="flex-1 p-4 bg-gray-800/50 border border-gray-700 rounded-xl hover:border-gray-600 transition-colors cursor-context-menu"
                  onContextMenu={(e) => handleMessageContextMenu(e, msg)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-purple-400">{msg.author}</span>
                    <span className="text-xs text-gray-500">{formatDiscussionTime(msg.timestamp)}</span>
                  </div>

                  {/* Edit mode */}
                  {editingMessage === msg.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
                        rows={3}
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveEdit(msg.id)}
                          className="px-3 py-1 text-sm bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                          style={{ color: 'var(--color-text-on-accent)' }}
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-3 py-1 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {msg.content && <p className="text-gray-300 leading-relaxed">{msg.content}</p>}
                      {msg.image_data && (
                        <img
                          src={msg.image_data}
                          alt="Attached image"
                          className="max-w-md rounded-lg border border-gray-700 cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => setModalImageUrl(msg.image_data)}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <svg
                className="w-16 h-16 mx-auto mb-3 text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              <p className="text-gray-500">No discussion messages yet</p>
              <p className="text-sm text-gray-600 mt-1">Be the first to contribute to this discussion</p>
            </div>
          )}
        </div>
      </div>

      {/* Input at Bottom - Fixed Overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-6 pointer-events-none">
        <div className="max-w-3xl mx-auto pointer-events-auto">
          {/* Typing Indicator */}
          {typingUsers.size > 0 && (
            <div className="mb-2 flex items-center gap-2 text-sm text-gray-400 ml-1">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                <span className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                <span className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                              </div>
              <span>
                {Array.from(typingUsers).join(', ')} {typingUsers.size === 1 ? 'is' : 'are'} typing...
              </span>
            </div>
          )}

          {/* Image Preview */}
          {imagePreview && (
            <div className="mb-2 ml-1 relative inline-block">
              <img
                src={imagePreview}
                alt="Preview"
                className="max-h-20 rounded-lg border-4 border-gray-700 cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setModalImageUrl(imagePreview)}
              />
              <button
                onClick={handleRemoveImage}
                className="absolute -top-1 -right-1 p-0.5 bg-red-600 hover:bg-red-700 rounded-full text-white transition-colors"
                type="button"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-1000"></div>
            <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl shadow-2xl p-4 border border-gray-700" style={{ color: 'var(--color-text-on-accent)' }}>
                          <form onSubmit={handleSendMessage}>
                <div className="flex items-center space-x-4">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={message}
                      onChange={handleInputChange}
                      placeholder="Type your message..."
                      className="w-full px-5 py-3 bg-gray-900/50 border-2 border-gray-700 rounded-xl text-base text-gray-100 placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 transition-all duration-200 hover:border-gray-600 pr-12"
                      disabled={isSending}
                    />

                    {/* Image Upload Button - Inside Input */}
                    <label className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer p-1.5 rounded-lg hover:bg-gray-700/50 text-gray-400 hover:text-purple-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="hidden"
                        disabled={isSending}
                      />
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </label>
                  </div>
                  <SendButton disabled={(!message.trim() && !selectedImage) || isSending} />
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <MessageContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          options={[
            // Show edit/delete only if it's the user's message
            ...(contextMenu.message.author === user?.username ? [
              {
                label: 'Edit',
                icon: (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                ),
                onClick: () => handleEditMessage(contextMenu.message)
              },
              {
                label: 'Delete',
                icon: (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                ),
                onClick: () => handleDeleteMessage(contextMenu.message.id),
                danger: true
              }
            ] : []),
            // Always show "Use as answer"
            {
              label: 'Use as answer',
              icon: (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
              onClick: () => handleUseAsAnswer(contextMenu.message.id)
            }
          ]}
        />
      )}

      {/* Image Modal */}
      {modalImageUrl && (
        <ImageModal
          imageUrl={modalImageUrl}
          onClose={() => setModalImageUrl(null)}
        />
      )}
    </div>
  );
}
